import type { Lesson } from './types'

/**
 * The three rules of Meem Sakinah (م with sukun) - a smaller, parallel set
 * to the noon sakinah rules, but based on the lips (shafawi) since meem is
 * a labial letter. Every Arabic letter falls into exactly one of these
 * three categories.
 */
export const meemRulesLessons: Lesson[] = [
  {
    id: 'meem-ikhfa-shafawi',
    title: 'Ikhfa Shafawi (Labial Concealment)',
    summary: 'Before ب, meem sakinah is concealed with a light nasal hum through closed lips.',
    explanation: [
      'Ikhfa Shafawi ("lip concealment") applies only when meem sakinah is followed by ب. The lips are gently closed, as if pronouncing meem, and a nasal hum (ghunnah) is held for about two counts before releasing into the baa.',
      'This is the labial counterpart to iqlab in the noon rules - both are triggered specifically by ب.',
    ],
    colorLegend: [{ color: 'ikhfa', label: 'Meem concealed with a nasal hum' }],
    examples: [
      { arabic: 'تَرْمِيهِمْ بِحِجَارَةٍ', transliteration: 'tarmīhim biḥijārah', meaning: 'pelting them with stones', highlights: [{ text: 'مْ', color: 'ikhfa' }] },
      { arabic: 'هُمْ بِهِ', transliteration: 'hum bihī', meaning: 'they, in it', highlights: [{ text: 'مْ', color: 'ikhfa' }] },
    ],
  },
  {
    id: 'meem-idgham-shafawi',
    title: 'Idgham Shafawi (Labial Merging)',
    summary: 'Before another meem, the two merge into one doubled, nasalized meem.',
    explanation: [
      'Idgham Shafawi ("lip merging") applies when meem sakinah is followed by another م. The two letters merge into a single meem held firmly with a nasal hum for about two counts - also called Idgham Mithlayn (merging of identical letters).',
      'This is the most straightforward of the three meem rules to spot: just watch for meem followed by meem.',
    ],
    colorLegend: [{ color: 'idghamGhunnah', label: 'Two meems merged with a nasal hum' }],
    examples: [
      { arabic: 'لَهُمْ مَا', transliteration: 'lahum mā', meaning: 'for them is what', highlights: [{ text: 'مْ مَا', color: 'idghamGhunnah' }] },
      { arabic: 'كُنْتُمْ مُؤْمِنِينَ', transliteration: 'kuntum muʾminīn', meaning: 'you were believers', highlights: [{ text: 'مْ مُ', color: 'idghamGhunnah' }] },
    ],
  },
  {
    id: 'meem-izhar-shafawi',
    title: 'Izhar Shafawi (Labial Clarity)',
    summary: 'Before any of the other 26 letters, meem sakinah is pronounced clearly.',
    explanation: [
      'Izhar Shafawi ("lip clarity") applies to every letter other than ب and م - the remaining 26 letters of the alphabet. Meem sakinah is pronounced clearly and briefly, with the lips closing and releasing without any lingering hum.',
      'Extra care is usually given to two specific letters here, و and ف, since they are also formed with (or near) the lips and it is easy to accidentally blend meem into them the way ikhfa shafawi blends into ب - but with these two, the rule is still izhar: keep the meem sound clean and distinct.',
    ],
    colorLegend: [{ color: 'izhar', label: 'Meem pronounced clearly' }],
    examples: [
      { arabic: 'هُمْ فِيهَا خَالِدُونَ', transliteration: 'hum fīhā khālidūn', meaning: 'therein they abide eternally', highlights: [{ text: 'مْ', color: 'izhar' }] },
      { arabic: 'عَلَيْهِمْ وَلَا', transliteration: 'ʿalayhim wa lā', meaning: 'upon them, nor', highlights: [{ text: 'مْ', color: 'izhar' }] },
      { arabic: 'أَنْعَمْتَ عَلَيْهِمْ', transliteration: 'anʿamta ʿalayhim', meaning: 'You have favored', highlights: [{ text: 'مْتَ', color: 'izhar' }] },
    ],
  },
]
